import classNames from "classnames";
import { number } from "prop-types";
import { useEffect, useRef, useState } from "react";
import useOutsideAlerter from "../hooks/use_outside_alerter";
import { useWindowSize } from "./with_tooltip";

const clamp = (num: number, min: number, max: number) =>
	Math.min(Math.max(num, min), max);

export function WithControlledTooltip(props: {
	children: React.ReactNode;
	tooltip: JSX.Element | string;
	style: "Info" | "Error";
	disable_click?: boolean;
	disabled?: boolean;
}) {
	const [tooltip_x, setTooltipX] = useState<number>(0);
	const [arrow_x, setArrowX] = useState<number>(0);

	const windowSize = useWindowSize();

	const tooltip_ref = useRef<HTMLDivElement>(null);
	const parent_ref = useRef<HTMLDivElement>(null);
	const arrow_ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (
			!tooltip_ref?.current ||
			!parent_ref?.current ||
			!windowSize ||
			!arrow_ref?.current ||
			!windowSize.width
		) {
			return;
		}

		const { width: t_width } = tooltip_ref.current.getBoundingClientRect();

		const { left: p_left, width: p_width } =
			parent_ref.current.getBoundingClientRect();

		// Hack to determine border length, since can't set child relative to border
		const border_offset = getComputedStyle(
			tooltip_ref.current
		).borderLeftWidth;

		const border_thickness = Number.parseInt(
			border_offset.substring(0, border_offset.length - 2)
		);

		const { width: a_width } = arrow_ref.current.getBoundingClientRect();

		const desired_absolute_tooltip_x = p_left + p_width / 2 - t_width / 2;

		const absolute_tooltip_x = clamp(
			desired_absolute_tooltip_x,
			0,
			windowSize.width - t_width
		);
		const relative_tooltip_x = absolute_tooltip_x - p_left;

		setTooltipX(relative_tooltip_x);

		const desired_absolute_arrow_x = p_left + p_width / 2 - a_width / 2;
		const absolute_arrow_x = clamp(
			desired_absolute_arrow_x,
			absolute_tooltip_x,
			absolute_tooltip_x + t_width
		);

		const relative_arrow_x = absolute_arrow_x - absolute_tooltip_x;

		// it is one pixel off for some reason
		setArrowX(-border_thickness + relative_arrow_x + 1);
	}, [tooltip_ref, parent_ref, arrow_ref, windowSize, props.disabled]);

	return (
		<div
			className={"dropdown inline-flex w-fit max-w-full items-center"}
			ref={parent_ref}
		>
			<div className="dropdown-trigger max-w-full flex-grow">
				<div
					className="max-w-full flex-grow cursor-pointer"
					aria-haspopup="true"
					aria-controls="dropdown-menu4"
				>
					{props.children}
				</div>
			</div>
			<div
				style={{
					left: tooltip_x,
				}}
				className={classNames(
					"has-text-centered absolute z-20 mb-2 flex h-max w-max max-w-[15rem] overflow-auto rounded-sm p-2 text-xs font-semibold drop-shadow-xl transition-opacity sm:max-h-[24rem] sm:max-w-[24rem] sm:!p-3 sm:text-sm",
					{
						"tooltip-v2": props.style === "Info",
						"tooltip-v2--error": props.style === "Error",
						hidden: props.disabled,
					}
				)}
				id="dropdown-menu4"
				role="menu"
				ref={tooltip_ref}
			>
				<span className="pointer-events-none overflow-auto">
					{props.tooltip}
				</span>
				<div
					className="tooltip-arrow"
					ref={arrow_ref}
					style={{ left: arrow_x }}
				></div>
			</div>
		</div>
	);
}
